import React from "react";
import { useController, useFormContext } from "react-hook-form";

import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import { SelectChangeEvent } from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";

import { SelectInputProps } from "./types";

import { grey000, grey200 } from "styles";

const StyledFormSelect = <T,>(props: SelectInputProps<T>) => {
  const {
    name,
    title,
    rules,
    helperText,
    defaultValue = "",
    options,
    fixedFooterOption,
    fixedFooterContent,
    secure,
    testId,
    onSelectItem,
    ...inputProps
  } = props;

  const ua = navigator.userAgent;
  const isSafari = /Safari/i.test(ua) && !/Chrome|Android|Chromium/i.test(ua);

  const formContext = useFormContext();
  const {
    control,
    formState: { errors: { [name]: { message = "" } = {} } = {} },
  } = formContext;

  const {
    field: { value = "", onChange },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const handleChange = (event: SelectChangeEvent) => {
    const allOptions = fixedFooterOption
      ? [...options, fixedFooterOption]
      : options;

    const selectedOption = allOptions.find(
      (option) => option.value === event.target.value,
    );

    onSelectItem && onSelectItem(selectedOption?.item as T);
    onChange(event.target.value);
  };

  const renderValue = (selectedValue: string) => {
    const allOptions = fixedFooterOption
      ? [...options, fixedFooterOption]
      : options;

    const selectedOption = allOptions.find(
      (option) => option.value === selectedValue,
    );

    return selectedOption ? selectedOption.label : selectedValue;
  };

  return (
    <Box mb={2} textAlign="left">
      {title && (
        <Typography variant="subtitle2" color="grey800" mb={1}>
          {title}
        </Typography>
      )}
      <FormControl error={!!message} {...inputProps}>
        <Select
          data-testid={testId}
          MenuProps={{
            transitionDuration: isSafari && fixedFooterOption ? 0 : "auto",
            PaperProps: { sx: { p: "0 !important", maxHeight: "80vh" } },
            sx: {
              "& .MuiMenu-list": fixedFooterOption ? { paddingBottom: 0 } : {},
            },
          }}
          value={value}
          onChange={handleChange}
          renderValue={renderValue}
          data-posthog-mask={secure ? "true" : undefined}
        >
          {options.map((option, index) => (
            <MenuItem
              key={`select-${name}-${index}`}
              value={option.value}
              data-posthog-mask={secure ? "true" : undefined}
              sx={{ p: "8px 16px" }}
            >
              <Typography noWrap>{option.label}</Typography>
            </MenuItem>
          ))}
          {fixedFooterOption && (
            <MenuItem
              key={`select-${name}-fixed-footer`}
              value={fixedFooterOption.value}
              sx={styles.menuItem}
              data-posthog-mask={secure ? "true" : undefined}
            >
              {fixedFooterContent || fixedFooterOption.label}
            </MenuItem>
          )}
        </Select>
        <FormHelperText data-posthog-mask={secure ? "true" : undefined}>
          {typeof message === "string" && message ? message : helperText || ""}
        </FormHelperText>
      </FormControl>
    </Box>
  );
};

const styles = {
  menuItem: {
    position: "sticky",
    bottom: 0,
    borderTop: "1px #EDEDED solid",
    backgroundColor: grey000,
    padding: "8px 16px",
    "&:hover": {
      backgroundColor: grey200,
    },
    "&.Mui-selected": {
      backgroundColor: "#EBEBFF",
      "&:hover": {
        backgroundColor: "#DFDFFF",
      },
    },
  },
};

export default StyledFormSelect;
